import { __assign, __rest } from "tslib";
// DS: PO72m
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import readableFileSize from 'filesize';
import ddt from '@wix/da-ddt';
import { Url } from '@wix/da-url';
import { BiEventType, ReferralType } from '@wix/da-bi/pkg/constants';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import { formatDollars } from '../../../../utils/formatting';
import { useRestrictToLoggedIn } from '../../../../utils/hooks/useRestrictToLoggedIn';
import { useSubscriptionTier } from '../../../../utils/hooks/useSubscriptionTier';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import ModalDialog from '../../../../Popup/ModalDialog';
import useLeaveACommentNotification from '../useLeaveACommentNotification';
import s from './MobileFreeAndPcpPopover.scss';
/** a ModalDialog that shows free and/or premium download info for the given deviation */
var MobileFreeAndPcpPopover = function (_a) {
    var deviation = _a.deviation, _b = _a.deviation.extended, _c = _b === void 0 ? {} : _b, _d = _c.pcp, pcp = _d === void 0 ? {} : _d, _e = _c.download, download = _e === void 0 ? {} : _e, requestClose = _a.requestClose;
    var subproductId = deviation.pcp.subproductId;
    var t = useTranslation().t;
    var _f = useSubscriptionTier(deviation), tier = _f.tier, isUserSubscribed = _f.isUserSubscribed, needsSubscription = _f.needsSubscription, isSubscribedButBlocked = _f.isSubscribedButBlocked, subscriptionsTabUrl = _f.subscriptionsTabUrl;
    var hasAccessViaSubscription = !!tier && isUserSubscribed && !isSubscribedButBlocked;
    var addLeaveACommentNotification = useLeaveACommentNotification(deviation);
    var closeModalAndNotify = useCallback(function () {
        requestClose && requestClose();
        addLeaveACommentNotification();
    }, [requestClose, addLeaveACommentNotification]);
    // We're only showing this popup if premium content exists, so it's bad if we make it here.
    if (!pcp) {
        ddtAlertForMissingPcp(deviation);
        return null;
    }
    var title = download.url
        ? t('actionbar.mobilePcp.freeAndPremiumDownload')
        : t('actionbar.mobilePcp.premiumDownload');
    var checkoutUrl = subproductId ? Url.checkoutLink(subproductId) : undefined;
    var shouldUsePoints = pcp.downloadPrice && pcp.downloadPrice < 80;
    var formattedDollars = formatDollars(pcp.downloadDollarPrice);
    var price = shouldUsePoints
        ? t('actionbar.mobilePcp.points', {
            price: pcp.downloadPrice,
            dollars: formattedDollars,
        })
        : formattedDollars;
    var canDownloadFree = download.url && !isSubscribedButBlocked;
    var canDownloadPcp = hasAccessViaSubscription || pcp.hasUserPurchased;
    return (React.createElement(ModalDialog, { isOpen: true, onClose: requestClose, title: title, titleClassName: s['title'], headerClassName: s['header'], contentClassName: s['content'], footerClassName: s['footer'], footer: React.createElement(TextButton, { variant: "main", size: "medium", onClick: requestClose, className: s['cancel-button'], noHorizontalPaddings: false }, t('common.cancel')) },
        canDownloadFree && (React.createElement(React.Fragment, null,
            React.createElement(Section, null,
                React.createElement(Heading, { className: s['subtitle'] }, t('actionbar.mobilePcp.freeDownload')),
                React.createElement("div", { className: s['file'] },
                    React.createElement("span", { className: s['resolution'] },
                        download.width,
                        "x",
                        download.height,
                        "px"),
                    React.createElement("span", { className: s['filesize'] },
                        "(",
                        readableFileSize(download.filesize || 0),
                        ")")),
                needsSubscription ? (React.createElement(DownloadLink, { href: subscriptionsTabUrl }, t('actionbar.download.mustSubscribe'))) : (React.createElement(DownloadLink, { download: true, href: download.url, onClick: closeModalAndNotify, biData: {
                        evid: BiEventType.CLICK_DOWNLOAD_BUTTON,
                        deviationid: deviation.deviationId,
                        ispaiddownload: 0,
                    } }, t('common.download')))),
            React.createElement("hr", { className: s['divider'] }))),
        React.createElement(Section, null,
            React.createElement(Heading, { className: s['subtitle'] }, t('actionbar.mobilePcp.premiumDownload')),
            React.createElement("div", { className: s['filesize'] }, t('actionbar.mobilePcp.premiumSize', {
                size: readableFileSize(pcp.zipFilesize || 0),
            })),
            React.createElement("ul", { className: s['pcp-assets'] }, (pcp.assets || []).map(function (_a, i) {
                var filename = _a.filename, width = _a.width, height = _a.height, filesize = _a.filesize;
                return (React.createElement("li", { key: i + "-" + filename },
                    React.createElement("div", { className: s['file'] },
                        React.createElement("span", { className: s['filename'] }, filename),
                        width && height && (React.createElement("span", { className: s['resolution'] },
                            width,
                            "x",
                            height,
                            "px")),
                        filesize && (React.createElement("span", { className: s['filesize'] },
                            "(",
                            readableFileSize(filesize),
                            ")")))));
            })),
            React.createElement(DownloadLink, { download: canDownloadPcp, href: canDownloadPcp ? pcp.url : checkoutUrl, onClick: canDownloadPcp ? closeModalAndNotify : undefined, biData: {
                    evid: BiEventType.CLICK_DOWNLOAD_BUTTON,
                    deviationid: deviation.deviationId,
                    ispaiddownload: 1,
                } }, canDownloadPcp
                ? t('common.download')
                : t('actionbar.mobilePcp.download.notPaid', { price: price })))));
};
MobileFreeAndPcpPopover.displayName = 'MobileFreeAndPcpPopover';
export default MobileFreeAndPcpPopover;
function ddtAlertForMissingPcp(deviation) {
    ddt.alert('pcp', 'Showing the FreeAndPcpPopover while the pcp field on the relevant deviation is undefined. This should not happen.', ['pcp', 'download'], { deviation: deviation });
}
/** a local download link that's restricted to logged in users */
var DownloadLink = function (props) {
    var href = props.href, download = props.download, onClick = props.onClick, children = props.children, remainingProps = __rest(props, ["href", "download", "onClick", "children"]);
    var _a = useRestrictToLoggedIn(), isLoggedIn = _a.isLoggedIn, restrictToLoggedIn = _a.restrictToLoggedIn;
    return (React.createElement(GhostButton, __assign({ className: s['download-button'], size: "medium", variant: "main", href: isLoggedIn ? href : undefined, download: isLoggedIn && download, onClick: restrictToLoggedIn(ReferralType.ANON_DOWNLOAD, onClick) }, remainingProps), children));
};
DownloadLink.displayName = 'DownloadLink';
//# sourceMappingURL=MobileFreeAndPcpPopover.js.map